/**
 * Progressive (infinite) loading for Featured Collection sections.
 *
 * For every <featured-collection data-infinite-url="..."> element, watches the
 * last slide/grid item with an IntersectionObserver. When it comes near the
 * viewport (works for Swiper carousels, native mobile swipe and plain grids),
 * it fetches the next page of products through the Section Rendering API
 * (sections/api-featured-collection-items.liquid), dedupes them against the
 * already rendered cards by product id (data-pid) and appends them before
 * notifying the theme's Swiper instance.
 */
(function () {
  // The same asset may be included by several sections - run once.
  if (window.__infiniteCollectionLoaded) return;
  window.__infiniteCollectionLoaded = true;

  var API_SECTION_ID = 'api-featured-collection-items';
  var processed = new WeakSet();

  function setup(el) {
    if (processed.has(el)) return;
    processed.add(el);

    var url = el.dataset.infiniteUrl;
    var wrapper = el.querySelector('.featured-collection__items');
    if (!url || !wrapper) return;

    var pageSize = parseInt(el.dataset.infinitePageSize, 10) || 12;
    var total = parseInt(el.dataset.infiniteTotal, 10) || Infinity;
    var rendered = parseInt(el.dataset.infiniteRendered, 10) || 0;

    // Product ids already in the carousel (server-rendered cards carry data-pid).
    var pids = new Set();
    wrapper.querySelectorAll('[data-pid]').forEach(function (n) {
      pids.add(n.getAttribute('data-pid'));
    });

    var state = {
      // Skip API pages fully covered by the server-rendered items
      // (e.g. limit 12 with a page size of 12 starts at page 2).
      nextPage: Math.floor(rendered / pageSize) + 1,
      maxPages: Infinity,
      loading: false,
      done: false,
    };

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            loadNext();
            break;
          }
        }
      },
      // Generous margins: horizontal for carousels, vertical for grids.
      { rootMargin: '300px 600px 300px 600px' }
    );

    function lastItem() {
      var items = wrapper.querySelectorAll(':scope > .f-column');
      return items.length ? items[items.length - 1] : null;
    }

    // (Re)observe the current last item. The observer fires immediately on
    // observe() if the item is already in range, which keeps loading chained.
    function watch() {
      observer.disconnect();
      var last = lastItem();
      if (last) observer.observe(last);
    }

    function finish() {
      state.done = true;
      observer.disconnect();
    }

    // Let the theme's Swiper instance (featured-collection.js) pick up the
    // appended slides; the resize event is a fallback for other observers.
    function notifySlider() {
      if (el.sliderInstance && el.sliderInstance.slider) {
        try {
          el.sliderInstance.slider.update();
        } catch (e) {
          /* noop */
        }
      }
      window.dispatchEvent(new Event('resize'));
    }

    function loadNext() {
      if (state.loading || state.done) return;
      if (state.nextPage > state.maxPages || pids.size >= total) return finish();
      state.loading = true;

      var sep = url.indexOf('?') === -1 ? '?' : '&';
      fetch(url + sep + 'section_id=' + API_SECTION_ID + '&page=' + state.nextPage)
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var container = doc.querySelector('[data-infinite-items]');
          if (!container) throw new Error('Missing items container');

          state.maxPages = parseInt(container.getAttribute('data-pages'), 10) || state.maxPages;
          var current = parseInt(container.getAttribute('data-page'), 10) || state.nextPage;
          state.nextPage = current + 1;

          // Mirror the classes the host section renders on its own slides.
          var slideClass = 'f-column' + (el.dataset.enableSlider === 'true' ? ' swiper-slide' : '');

          var frag = document.createDocumentFragment();
          container.querySelectorAll('[data-pid]').forEach(function (item) {
            var pid = item.getAttribute('data-pid');
            if (!pid || pids.has(pid)) return; // dedupe against rendered cards
            pids.add(pid);
            item.className = slideClass;
            frag.appendChild(item);
          });

          if (frag.childNodes.length) {
            wrapper.appendChild(frag);
            notifySlider();
          }

          state.loading = false;
          if (state.nextPage > state.maxPages || pids.size >= total) {
            finish();
          } else {
            watch();
          }
        })
        .catch(function () {
          // Fail quietly: leave the section exactly as server-rendered.
          state.loading = false;
          finish();
        });
    }

    watch();
  }

  function scan(root) {
    (root || document).querySelectorAll('featured-collection[data-infinite-url]').forEach(setup);
  }

  function init() {
    scan(document);

    // Non-first product-tabs panels are stamped from <template> on demand -
    // watch the DOM so their carousels get wired up too.
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('featured-collection[data-infinite-url]')) {
            setup(node);
          } else if (node.querySelectorAll) {
            scan(node);
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // Theme editor support: re-scan re-rendered sections.
    document.addEventListener('shopify:section:load', function (e) {
      scan(e.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
